import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Substack Export Tab Component
function SubstackExportTab({ series }) {
  const [selectedSeries, setSelectedSeries] = useState('');
  const [chapters, setChapters] = useState([]);
  const [exportHtml, setExportHtml] = useState('');
  const [exportMeta, setExportMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadChapters = async (slug) => {
    setSelectedSeries(slug);
    setExportHtml('');
    setExportMeta(null);
    if (!slug) { setChapters([]); return; }
    try {
      const res = await axios.get(`${API}/api/admin/substack-export/${slug}`);
      setChapters(res.data.chapters || []);
    } catch { setChapters([]); }
  };

  const exportChapter = async (chNum) => {
    setLoading(true);
    setCopied(false);
    try {
      const res = await axios.get(`${API}/api/admin/substack-export/${selectedSeries}/${chNum}`);
      setExportHtml(res.data.substack_html);
      setExportMeta(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(exportHtml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  return (
    <div data-testid="substack-tab">
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 20px' }}>Substack Export</h2>
      <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px' }}>
        Generate Substack-ready HTML for any chapter. Copy and paste directly into the Substack editor (use "Import" or paste as HTML).
      </p>

      {/* Series Picker */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div>
          <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px', letterSpacing: '1px', textTransform: 'uppercase' }}>Select Series</label>
          <select
            value={selectedSeries}
            onChange={(e) => loadChapters(e.target.value)}
            data-testid="substack-series-select"
            style={{ padding: '10px 14px', background: '#070c2a', border: '1px solid #1a2155', borderRadius: '6px', color: '#FFFFFF', fontFamily: 'Jost, sans-serif', fontSize: '14px', width: '100%' }}
          >
            <option value="">Choose a series...</option>
            {series.map(s => (
              <option key={s.slug} value={s.slug}>{s.title} ({s.total_chapters} chapters)</option>
            ))}
          </select>
        </div>
      </div>

      {/* Chapters List */}
      {chapters.length > 0 && (
        <div style={{ background: '#0f1545', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <p style={{ fontSize: '12px', color: '#888', letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 12px' }}>Chapters</p>
          <div style={{ display: 'grid', gap: '4px' }}>
            {chapters.map(ch => (
              <button
                key={ch.chapter_number}
                onClick={() => exportChapter(ch.chapter_number)}
                data-testid={`substack-ch-${ch.chapter_number}`}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', background: exportMeta?.chapter_number === ch.chapter_number ? '#1a2155' : 'transparent',
                  border: '1px solid transparent', borderRadius: '4px', cursor: 'pointer',
                  color: '#FFFFFF', fontFamily: 'Jost, sans-serif', fontSize: '14px', textAlign: 'left', width: '100%',
                }}
              >
                <span>Ch {ch.chapter_number}: {ch.title}</span>
                <span style={{ color: '#666', fontSize: '12px' }}>{ch.word_count.toLocaleString()} words</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Export Preview */}
      {loading && <p style={{ color: '#888', fontSize: '14px' }}>Generating export...</p>}
      {exportMeta && exportHtml && (
        <div style={{ background: '#0f1545', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #1a2155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '14px', color: '#FFFFFF', margin: '0 0 4px' }}>
                <strong>{exportMeta.series_title}</strong> &middot; Ch {exportMeta.chapter_number}: {exportMeta.title}
              </p>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                Subject line: <em>{exportMeta.subject_line}</em> &middot; {exportMeta.word_count.toLocaleString()} words
              </p>
            </div>
            <button
              onClick={copyToClipboard}
              data-testid="substack-copy-btn"
              style={{
                padding: '8px 20px', background: copied ? '#2e8b57' : '#C9A227', color: '#090E33',
                border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: 'Jost, sans-serif',
                fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px',
              }}
            >
              {copied ? 'Copied!' : 'Copy HTML'}
            </button>
          </div>
          <div style={{ padding: '20px', maxHeight: '400px', overflow: 'auto' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', color: '#888', fontFamily: 'monospace', lineHeight: '1.6' }}>
              {exportHtml}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}



export default function AdminPage() {
  const { user, token, login } = useAuth();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);

  // Admin login form (shown when not logged in or not admin)
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // Data
  const [series, setSeries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [mailingLog, setMailingLog] = useState([]);
  const [audioStatuses, setAudioStatuses] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [sendingChapter, setSendingChapter] = useState(null);
  const [toast, setToast] = useState(null);

  // Forms
  const [newSubEmail, setNewSubEmail] = useState('');
  const [newSubSeries, setNewSubSeries] = useState('');
  const [scheduleForm, setScheduleForm] = useState({ series_slug: '', day_of_week: 'tuesday', start_chapter: 1, time_utc: '14:00' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSlug, setUploadSlug] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Check admin authorization
  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) { setAuthChecking(false); return; }
      try {
        await axios.get(`${API}/api/admin/auth-check`);
        setIsAuthorized(true);
      } catch {
        setIsAuthorized(false);
      } finally {
        setAuthChecking(false);
      }
    };
    checkAdmin();
  }, [token]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await login(adminEmail, adminPassword);
      // After login, re-check admin status
      try {
        await axios.get(`${API}/api/admin/auth-check`);
        setIsAuthorized(true);
      } catch {
        setAuthError('This account does not have admin access.');
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Invalid credentials');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [seriesRes, subsRes, schedRes, logRes] = await Promise.all([
        axios.get(`${API}/api/admin/series`),
        axios.get(`${API}/api/admin/subscribers`),
        axios.get(`${API}/api/admin/schedules`),
        axios.get(`${API}/api/admin/mailing-log`),
      ]);
      setSeries(seriesRes.data);
      setSubscribers(subsRes.data);
      setSchedules(schedRes.data);
      setMailingLog(logRes.data);

      // Fetch audio status for each series
      const statuses = {};
      for (const s of seriesRes.data) {
        try {
          const audioRes = await axios.get(`${API}/api/audio/status/${s.slug}`);
          statuses[s.slug] = audioRes.data;
        } catch {
          statuses[s.slug] = { generated: [], missing: [], generated_count: 0, total_chapters: s.total_chapters, complete: false };
        }
      }
      setAudioStatuses(statuses);

      // Fetch analytics
      try {
        const analyticsRes = await axios.get(`${API}/api/admin/analytics`);
        setAnalytics(analyticsRes.data);
      } catch { /* analytics optional */ }
    } catch (e) {
      console.error('Failed to fetch admin data', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAuthorized) fetchData(); }, [isAuthorized, fetchData]);

  // Handlers
  const handleSendChapter = async (seriesSlug, chapterNum) => {
    setSendingChapter(`${seriesSlug}-${chapterNum}`);
    try {
      const res = await axios.post(`${API}/api/admin/send-chapter`, { series_slug: seriesSlug, chapter_number: chapterNum });
      showToast(res.data.message);
      fetchData();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to send', 'error');
    } finally {
      setSendingChapter(null);
    }
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/admin/schedule`, scheduleForm);
      showToast('Schedule created');
      fetchData();
      setScheduleForm({ series_slug: '', day_of_week: 'tuesday', start_chapter: 1, time_utc: '14:00' });
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to create schedule', 'error');
    }
  };

  const handleToggleSchedule = async (slug) => {
    try {
      const res = await axios.post(`${API}/api/admin/schedule/${slug}/toggle`);
      showToast(res.data.active ? 'Schedule activated' : 'Schedule paused');
      fetchData();
    } catch (e) {
      showToast('Failed to toggle schedule', 'error');
    }
  };

  const handleDeleteSchedule = async (slug) => {
    try {
      await axios.delete(`${API}/api/admin/schedule/${slug}`);
      showToast('Schedule deleted');
      fetchData();
    } catch (e) {
      showToast('Failed to delete schedule', 'error');
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!newSubEmail || !newSubSeries) return;
    try {
      await axios.post(`${API}/api/admin/subscribers/series`, { email: newSubEmail, series_slug: newSubSeries });
      showToast('Subscriber added');
      setNewSubEmail('');
      fetchData();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to add subscriber', 'error');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadSlug || !uploadTitle) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('series_slug', uploadSlug);
      formData.append('series_title', uploadTitle);
      const res = await axios.post(`${API}/api/admin/upload-manuscript`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast(`Parsed ${res.data.chapters_parsed} chapters`);
      setUploadFile(null); setUploadSlug(''); setUploadTitle('');
      fetchData();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAudio = async (seriesSlug, chapterNum = null) => {
    const key = chapterNum ? `${seriesSlug}-${chapterNum}` : seriesSlug;
    setGeneratingAudio(key);
    try {
      const payload = { series_slug: seriesSlug };
      if (chapterNum) payload.chapter_number = chapterNum;
      const res = await axios.post(`${API}/api/admin/generate-audio`, payload);
      showToast(res.data.message);
      // Refresh audio status after a short delay
      setTimeout(fetchData, 2000);
    } catch (e) {
      showToast(e.response?.data?.detail || 'Audio generation failed', 'error');
    } finally {
      setGeneratingAudio(null);
    }
  };

  // AUTH GATE: Show login form if not authorized
  if (authChecking) {
    return (
      <div style={{ minHeight: '100vh', background: '#090E33', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#FFFFFF', fontFamily: 'Jost, sans-serif', fontSize: '18px' }}>Checking access...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div data-testid="admin-login" style={{ minHeight: '100vh', background: '#090E33', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: '360px', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <span style={{ fontSize: '12px', letterSpacing: '3px', textTransform: 'uppercase', color: '#FF4343' }}>Freddy Fiction</span>
            <h1 style={{ margin: '8px 0 0', fontSize: '24px', fontFamily: 'Playfair Display, serif', color: '#FFFFFF' }}>Admin Access</h1>
          </div>
          <form onSubmit={handleAdminLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px', letterSpacing: '1px', textTransform: 'uppercase' }}>Email</label>
              <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} required autoFocus style={inputStyle} data-testid="admin-login-email" />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px', letterSpacing: '1px', textTransform: 'uppercase' }}>Password</label>
              <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} required style={inputStyle} data-testid="admin-login-password" />
            </div>
            {authError && <p style={{ color: '#c0392b', fontSize: '14px', textAlign: 'center', margin: 0 }} data-testid="admin-login-error">{authError}</p>}
            <button type="submit" style={btnStyle} data-testid="admin-login-btn">Sign In</button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '24px' }}>
            <a href="/" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>&larr; Back to site</a>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#090E33', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#FFFFFF', fontFamily: 'Jost, sans-serif', fontSize: '18px' }}>Loading admin...</div>
      </div>
    );
  }

  const tabStyle = (tab) => ({
    padding: '10px 20px',
    cursor: 'pointer',
    borderBottom: activeTab === tab ? '2px solid #FF4343' : '2px solid transparent',
    color: activeTab === tab ? '#FFFFFF' : '#666',
    background: 'none',
    border: 'none',
    fontFamily: 'Jost, sans-serif',
    fontSize: '14px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    transition: 'color 0.2s'
  });

  return (
    <div data-testid="admin-dashboard" style={{ minHeight: '100vh', background: '#090E33', color: '#FFFFFF', fontFamily: 'Jost, sans-serif' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          padding: '12px 24px', borderRadius: '6px',
          background: toast.type === 'error' ? '#FF4343' : '#1a3a1a',
          color: '#FFFFFF', fontSize: '14px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ borderBottom: '1px solid #1a2155', padding: '20px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontSize: '12px', letterSpacing: '3px', textTransform: 'uppercase', color: '#FF4343' }}>Freddy Fiction</span>
          <h1 style={{ margin: '4px 0 0', fontSize: '24px', fontFamily: 'Playfair Display, serif', color: '#FFFFFF' }}>Admin Dashboard</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ color: '#666', fontSize: '13px' }}>{user?.email}</span>
          <a href="/" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>&larr; Back to site</a>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', padding: '0 40px', borderBottom: '1px solid #1a2155', overflowX: 'auto' }}>
        {['overview', 'analytics', 'audio', 'schedules', 'subscribers', 'send', 'upload', 'substack'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={tabStyle(tab)} data-testid={`tab-${tab}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '30px 40px', maxWidth: '1000px' }}>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div data-testid="overview-tab">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 20px' }}>Series Overview</h2>
            <div style={{ display: 'grid', gap: '16px' }}>
              {series.map(s => {
                const audio = audioStatuses[s.slug];
                return (
                  <div key={s.slug} style={{ background: '#0f1545', borderRadius: '8px', padding: '20px', borderLeft: `3px solid ${s.accent_color}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '18px', fontFamily: 'Playfair Display, serif' }}>{s.title}</h3>
                        <p style={{ margin: '4px 0 0', color: '#888', fontSize: '14px' }}>
                          {s.loaded_chapters}/{s.total_chapters} chapters &middot; {s.subscriber_count} subscribers
                          {audio && <span> &middot; {audio.generated_count}/{audio.total_chapters} audio</span>}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {s.schedule ? (
                          <span style={{ fontSize: '13px', color: s.schedule.active ? '#4caf50' : '#888' }}>
                            {s.schedule.active ? 'Scheduled' : 'Paused'}: {s.schedule.day_of_week}s at {s.schedule.time_utc} UTC
                            <br />Ch {s.schedule.current_chapter} next
                          </span>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#666' }}>No schedule</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '30px 0 16px' }}>Recent Sends</h2>
            {mailingLog.length === 0 ? (
              <p style={{ color: '#666', fontSize: '14px' }}>No emails sent yet.</p>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {mailingLog.slice(0, 5).map((log, i) => (
                  <div key={i} style={{ background: '#0f1545', borderRadius: '6px', padding: '14px 20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{log.series_slug} &middot; Chapter {log.chapter_number}: {log.chapter_title}</span>
                    <span style={{ color: '#888' }}>{log.recipient_count} recipients &middot; {new Date(log.sent_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div data-testid="analytics-tab">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 20px' }}>Analytics</h2>

            {!analytics ? (
              <p style={{ color: '#888', fontSize: '14px' }}>Loading analytics...</p>
            ) : (
              <>
                {/* KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                  {[
                    { label: 'Total Users', value: analytics.users.total, color: '#FF4343' },
                    { label: 'Free', value: analytics.users.free, color: '#4682b4' },
                    { label: 'Reader ($5)', value: analytics.users.reader, color: '#c8860a' },
                    { label: 'Patron ($9.99)', value: analytics.users.patron, color: '#d4a574' },
                    { label: 'Active Subscribers', value: analytics.subscribers.total_active, color: '#2e8b57' },
                    { label: 'Total Revenue', value: `$${analytics.payments.total_revenue.toFixed(2)}`, color: '#7d3c98' },
                  ].map((kpi) => (
                    <div key={kpi.label} data-testid={`kpi-${kpi.label.toLowerCase().replace(/[\s()$/.]/g, '-')}`} style={{
                      background: '#0f1545', borderRadius: '8px', padding: '20px',
                      borderLeft: `3px solid ${kpi.color}`,
                    }}>
                      <p style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px' }}>{kpi.label}</p>
                      <p style={{ fontSize: '28px', fontFamily: 'Playfair Display, serif', color: '#FFFFFF', margin: 0 }}>{kpi.value}</p>
                    </div>
                  ))}
                </div>

                {/* Engagement Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ background: '#0f1545', borderRadius: '8px', padding: '20px' }}>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', margin: '0 0 16px' }}>Community</h3>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span style={{ color: '#888' }}>Total Comments</span>
                        <span data-testid="stat-total-comments">{analytics.engagement.total_comments}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span style={{ color: '#888' }}>Comments (last 7 days)</span>
                        <span data-testid="stat-recent-comments">{analytics.engagement.recent_comments_7d}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span style={{ color: '#888' }}>Paid Transactions</span>
                        <span data-testid="stat-total-payments">{analytics.payments.total_paid}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ background: '#0f1545', borderRadius: '8px', padding: '20px' }}>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', margin: '0 0 16px' }}>Listening</h3>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span style={{ color: '#888' }}>Active Listeners</span>
                        <span data-testid="stat-active-listeners">{analytics.engagement.active_listeners}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span style={{ color: '#888' }}>Total Playback Saves</span>
                        <span data-testid="stat-playback-saves">{analytics.engagement.total_playback_saves}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Series Popularity */}
                <div style={{ background: '#0f1545', borderRadius: '8px', padding: '20px', marginBottom: '32px' }}>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', margin: '0 0 16px' }}>Series Popularity</h3>
                  {analytics.series_popularity.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>No reading data yet.</p>
                  ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {analytics.series_popularity.map((sp) => {
                        const maxReaders = Math.max(...analytics.series_popularity.map(s => s.unique_readers), 1);
                        const pct = Math.round((sp.unique_readers / maxReaders) * 100);
                        return (
                          <div key={sp.series_slug} data-testid={`popularity-${sp.series_slug}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                              <span style={{ color: sp.accent }}>{sp.title}</span>
                              <span style={{ color: '#888' }}>{sp.unique_readers} reader{sp.unique_readers !== 1 ? 's' : ''} &middot; {sp.total_reads} reads</span>
                            </div>
                            <div style={{ height: '6px', background: '#070c2a', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: sp.accent, borderRadius: '3px', transition: 'width 0.5s' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Signups Chart (simple bar) */}
                {analytics.signups_by_day.length > 0 && (
                  <div style={{ background: '#0f1545', borderRadius: '8px', padding: '20px', marginBottom: '32px' }}>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', margin: '0 0 16px' }}>Signups (Last 30 Days)</h3>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '100px' }}>
                      {analytics.signups_by_day.map((d) => {
                        const maxCount = Math.max(...analytics.signups_by_day.map(s => s.count), 1);
                        const h = Math.max(Math.round((d.count / maxCount) * 100), 4);
                        return (
                          <div key={d.date} title={`${d.date}: ${d.count} signups`} style={{
                            flex: 1, height: `${h}%`, background: '#FF4343', borderRadius: '2px 2px 0 0',
                            minWidth: '4px', transition: 'height 0.3s',
                          }} />
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginTop: '6px' }}>
                      <span>{analytics.signups_by_day[0]?.date?.slice(5)}</span>
                      <span>{analytics.signups_by_day[analytics.signups_by_day.length - 1]?.date?.slice(5)}</span>
                    </div>
                  </div>
                )}

                {/* Recent Users */}
                <div style={{ background: '#0f1545', borderRadius: '8px', padding: '20px' }}>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', margin: '0 0 16px' }}>Recent Signups</h3>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {analytics.recent_users.map((u, i) => (
                      <div key={i} data-testid={`recent-user-${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', padding: '8px 0', borderBottom: i < analytics.recent_users.length - 1 ? '1px solid #1a2155' : 'none' }}>
                        <div>
                          <span style={{ color: '#FFFFFF' }}>{u.name || u.email.split('@')[0]}</span>
                          <span style={{ color: '#666', marginLeft: '8px', fontSize: '12px' }}>{u.email}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '12px',
                            background: u.tier === 'patron' ? '#FF4343' : u.tier === 'reader' ? '#c8860a33' : '#222',
                            color: u.tier === 'patron' ? '#FFFFFF' : u.tier === 'reader' ? '#c8860a' : '#888',
                          }}>{u.tier}</span>
                          <span style={{ color: '#666', fontSize: '12px' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                    ))}
                    {analytics.recent_users.length === 0 && (
                      <p style={{ color: '#666', fontSize: '14px' }}>No users yet.</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}


        {/* AUDIO TAB */}
        {activeTab === 'audio' && (
          <div data-testid="audio-tab">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 8px' }}>Audio Generation</h2>
            <p style={{ color: '#888', fontSize: '14px', margin: '0 0 24px' }}>Generate audiobook MP3s for chapters using OpenAI TTS. Audio is available for download by all logged-in users.</p>

            {series.map(s => {
              const audio = audioStatuses[s.slug] || { generated: [], missing: [], generated_count: 0, total_chapters: s.total_chapters };
              const pct = s.total_chapters > 0 ? Math.round((audio.generated_count / s.total_chapters) * 100) : 0;
              const isGenSeries = generatingAudio === s.slug;

              return (
                <div key={s.slug} style={{ marginBottom: '28px', background: '#0f1545', borderRadius: '8px', padding: '20px', borderLeft: `3px solid ${s.accent_color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '16px', fontFamily: 'Playfair Display, serif' }}>{s.title}</h3>
                      <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>
                        {audio.generated_count}/{audio.total_chapters} chapters generated
                        {audio.total_size_bytes > 0 && ` · ${(audio.total_size_bytes / (1024 * 1024)).toFixed(1)} MB`}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {audio.complete ? (
                        <span style={{ fontSize: '13px', color: '#4caf50', fontWeight: 500 }}>Complete</span>
                      ) : (
                        <button
                          onClick={() => handleGenerateAudio(s.slug)}
                          disabled={isGenSeries}
                          style={{ ...btnSmallStyle, opacity: isGenSeries ? 0.5 : 1 }}
                          data-testid={`gen-all-${s.slug}`}
                        >
                          {isGenSeries ? 'Generating...' : `Generate All (${audio.missing?.length || 0} missing)`}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ height: '4px', background: '#1a2155', borderRadius: '2px', marginBottom: '12px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: s.accent_color, borderRadius: '2px', transition: 'width 0.3s' }} />
                  </div>

                  {/* Chapter grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '6px' }}>
                    {Array.from({ length: s.total_chapters }, (_, i) => i + 1).map(num => {
                      const exists = audio.generated?.includes(num);
                      const isGen = generatingAudio === `${s.slug}-${num}`;
                      return (
                        <button
                          key={num}
                          onClick={() => !exists && handleGenerateAudio(s.slug, num)}
                          disabled={exists || isGen}
                          style={{
                            padding: '8px', background: exists ? `${s.accent_color}15` : '#070c2a',
                            border: `1px solid ${exists ? s.accent_color + '44' : '#1a2155'}`,
                            color: exists ? s.accent_color : '#888',
                            borderRadius: '4px', cursor: exists ? 'default' : 'pointer',
                            fontSize: '12px', textAlign: 'center', opacity: isGen ? 0.5 : 1,
                            transition: 'all 0.2s',
                          }}
                          data-testid={`audio-ch-${s.slug}-${num}`}
                          title={exists ? `Ch ${num} - Generated` : `Ch ${num} - Click to generate`}
                        >
                          Ch {num}
                          <span style={{ display: 'block', fontSize: '10px', marginTop: '2px' }}>
                            {isGen ? 'Gen...' : exists ? 'Ready' : 'Missing'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SCHEDULES TAB */}
        {activeTab === 'schedules' && (
          <div data-testid="schedules-tab">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 20px' }}>Mailing Schedules</h2>
            {schedules.length > 0 && (
              <div style={{ display: 'grid', gap: '12px', marginBottom: '30px' }}>
                {schedules.map(sch => {
                  const meta = series.find(s => s.slug === sch.series_slug);
                  return (
                    <div key={sch.series_slug} style={{ background: '#0f1545', borderRadius: '8px', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>{meta?.title || sch.series_slug}</strong>
                        <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>
                          Every {sch.day_of_week} at {sch.time_utc} UTC &middot; Next: Chapter {sch.current_chapter}
                          {sch.last_sent_at && ` · Last sent: ${new Date(sch.last_sent_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleToggleSchedule(sch.series_slug)} style={{ padding: '6px 14px', background: sch.active ? '#1a3a1a' : '#333', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }} data-testid={`toggle-${sch.series_slug}`}>
                          {sch.active ? 'Pause' : 'Resume'}
                        </button>
                        <button onClick={() => handleDeleteSchedule(sch.series_slug)} style={{ padding: '6px 14px', background: '#3a1a1a', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }} data-testid={`delete-${sch.series_slug}`}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ background: '#0f1545', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Create Schedule</h3>
              <form onSubmit={handleCreateSchedule} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Series</label>
                  <select value={scheduleForm.series_slug} onChange={e => setScheduleForm(f => ({ ...f, series_slug: e.target.value }))} required style={inputStyle} data-testid="schedule-series-select">
                    <option value="">Select series</option>
                    {series.map(s => <option key={s.slug} value={s.slug}>{s.title}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Day of week</label>
                  <select value={scheduleForm.day_of_week} onChange={e => setScheduleForm(f => ({ ...f, day_of_week: e.target.value }))} style={inputStyle} data-testid="schedule-day-select">
                    {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Start Chapter</label>
                  <input type="number" min="1" value={scheduleForm.start_chapter} onChange={e => setScheduleForm(f => ({ ...f, start_chapter: parseInt(e.target.value) || 1 }))} style={inputStyle} data-testid="schedule-start-chapter" />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Time (UTC)</label>
                  <input type="time" value={scheduleForm.time_utc} onChange={e => setScheduleForm(f => ({ ...f, time_utc: e.target.value }))} style={inputStyle} data-testid="schedule-time" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <button type="submit" style={btnStyle} data-testid="create-schedule-btn">Create Schedule</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* SUBSCRIBERS TAB */}
        {activeTab === 'subscribers' && (
          <div data-testid="subscribers-tab">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 20px' }}>Subscribers ({subscribers.length})</h2>
            <form onSubmit={handleAddSubscriber} style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <input type="email" placeholder="email@example.com" value={newSubEmail} onChange={e => setNewSubEmail(e.target.value)} required style={{ ...inputStyle, flex: 1 }} data-testid="add-subscriber-email" />
              <select value={newSubSeries} onChange={e => setNewSubSeries(e.target.value)} required style={{ ...inputStyle, width: '200px' }} data-testid="add-subscriber-series">
                <option value="">Select series</option>
                {series.map(s => <option key={s.slug} value={s.slug}>{s.title}</option>)}
              </select>
              <button type="submit" style={btnStyle} data-testid="add-subscriber-btn">Add</button>
            </form>
            <div style={{ background: '#0f1545', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1a2155' }}>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Tier</th>
                    <th style={thStyle}>Series</th>
                    <th style={thStyle}>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #1a2155' }}>
                      <td style={tdStyle}>{sub.email}</td>
                      <td style={tdStyle}>
                        <span style={{ background: sub.tier === 'paid' || sub.tier === 'patron' ? '#FF4343' : '#222', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                          {sub.tier || 'free'}
                        </span>
                      </td>
                      <td style={tdStyle}>{(sub.subscribed_series || []).join(', ') || 'all'}</td>
                      <td style={{ ...tdStyle, color: '#666' }}>{sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : '\u2014'}</td>
                    </tr>
                  ))}
                  {subscribers.length === 0 && (
                    <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#666' }}>No subscribers yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SEND TAB */}
        {activeTab === 'send' && (
          <div data-testid="send-tab">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 20px' }}>Send Chapter Emails</h2>
            {series.map(s => (
              <div key={s.slug} style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', margin: '0 0 12px', color: s.accent_color }}>{s.title}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px' }}>
                  {Array.from({ length: s.loaded_chapters }, (_, i) => i + 1).map(num => {
                    const key = `${s.slug}-${num}`;
                    const isSending = sendingChapter === key;
                    return (
                      <button key={num} onClick={() => handleSendChapter(s.slug, num)} disabled={isSending}
                        style={{ padding: '10px 14px', background: '#0f1545', border: '1px solid #1a2155', color: '#FFFFFF', borderRadius: '6px', cursor: isSending ? 'wait' : 'pointer', fontSize: '13px', textAlign: 'left', opacity: isSending ? 0.5 : 1, transition: 'border-color 0.2s' }}
                        data-testid={`send-${s.slug}-ch${num}`}
                      >
                        Ch {num}
                        <span style={{ display: 'block', fontSize: '11px', color: '#888', marginTop: '2px' }}>
                          {isSending ? 'Sending...' : `${s.subscriber_count} subs`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* UPLOAD TAB */}
        {activeTab === 'upload' && (
          <div data-testid="upload-tab">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', margin: '0 0 20px' }}>Upload Manuscript</h2>
            <div style={{ background: '#0f1545', borderRadius: '8px', padding: '24px' }}>
              <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px' }}>
                Upload a PDF manuscript. Chapters will be automatically detected and parsed.
              </p>
              <form onSubmit={handleUpload} style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Series Title</label>
                  <input type="text" placeholder="e.g., My New Novel" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} required style={inputStyle} data-testid="upload-title" />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>URL Slug</label>
                  <input type="text" placeholder="e.g., my-new-novel" value={uploadSlug} onChange={e => setUploadSlug(e.target.value)} required style={inputStyle} data-testid="upload-slug" />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>PDF File</label>
                  <input type="file" accept=".pdf" onChange={e => setUploadFile(e.target.files[0])} required style={{ ...inputStyle, padding: '8px' }} data-testid="upload-file" />
                </div>
                <button type="submit" disabled={uploading} style={{ ...btnStyle, opacity: uploading ? 0.5 : 1 }} data-testid="upload-btn">
                  {uploading ? 'Uploading & Parsing...' : 'Upload & Parse Chapters'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* SUBSTACK TAB */}
        {activeTab === 'substack' && (
          <SubstackExportTab series={series} />
        )}

      </div>
    </div>
  );
}

const inputStyle = {
  padding: '10px 14px',
  background: '#070c2a',
  border: '1px solid #1a2155',
  borderRadius: '6px',
  color: '#FFFFFF',
  fontFamily: 'Jost, sans-serif',
  fontSize: '14px',
  width: '100%',
  boxSizing: 'border-box'
};

const btnStyle = {
  padding: '10px 24px',
  background: '#FF4343',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily: 'Jost, sans-serif',
  fontSize: '14px',
  letterSpacing: '1px',
  textTransform: 'uppercase'
};

const btnSmallStyle = {
  padding: '6px 16px',
  background: '#FF4343',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: 'Jost, sans-serif',
  fontSize: '12px',
  letterSpacing: '0.5px',
};

const thStyle = { textAlign: 'left', padding: '12px 16px', color: '#888', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase' };
const tdStyle = { padding: '12px 16px' };
